<script setup lang="ts">
  import { computed } from 'vue';

  const props = defineProps<{
    planName: string;
    statusText: string;
    statusColor: string;
    currentAccessName: string;
    planExpiresAt: string | null;
  }>();

  function safeDateLabel(iso: string | null) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString();
  }

  const planExpiresLabel = computed(() => safeDateLabel(props.planExpiresAt));

  const statusTone = computed(() => {
    const color = (props.statusColor ?? '').toLowerCase();
    if (color === 'success') return 'success';
    if (color === 'warning') return 'warning';
    if (color === 'info') return 'info';
    return 'neutral';
  });

  const statusIcon = computed(() => {
    if (statusTone.value === 'success') return 'mdi-check-circle';
    if (statusTone.value === 'warning') return 'mdi-alert';
    if (statusTone.value === 'info') return 'mdi-information';
    return 'mdi-circle-outline';
  });
</script>

<template>
  <v-card class="billing-legacy-plan-status-card" elevation="0">
    <div class="billing-legacy-plan-status-card__header">
      <div class="billing-legacy-plan-status-card__title">
        <div class="billing-legacy-plan-status-card__eyebrow">
          Su plan actual
        </div>
        <div class="billing-legacy-plan-status-card__plan">{{ planName }}</div>
      </div>

      <div
        class="billing-legacy-plan-status-card__status"
        :class="`billing-legacy-plan-status-card__status--${statusTone}`"
      >
        <v-icon class="billing-legacy-plan-status-card__status-icon" size="18">
          {{ statusIcon }}
        </v-icon>
        <span class="billing-legacy-plan-status-card__status-text">
          {{ statusText }}
        </span>
      </div>
    </div>

    <v-card-text class="billing-legacy-plan-status-card__body">
      <div class="billing-legacy-plan-status-card__facts">
        <div class="billing-legacy-plan-status-card__fact">
          <div class="billing-legacy-plan-status-card__fact-label">
            Acceso actual
          </div>
          <div class="billing-legacy-plan-status-card__fact-value">
            {{ currentAccessName }}
          </div>
        </div>

        <div class="billing-legacy-plan-status-card__fact">
          <div class="billing-legacy-plan-status-card__fact-label">
            Acceso hasta
          </div>
          <div class="billing-legacy-plan-status-card__fact-value">
            {{ planExpiresLabel ? planExpiresLabel : '—' }}
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .billing-legacy-plan-status-card {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.08);
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 4px;
      width: 100%;
      background: rgba(255, 111, 0, 0.9);
    }

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      padding: 16px 16px 10px;
      background: linear-gradient(
        180deg,
        rgba(255, 111, 0, 0.08),
        rgba(255, 255, 255, 0)
      );
    }

    &__eyebrow {
      font-size: 12px;
      font-weight: 900;
      letter-spacing: 0.2px;
      color: rgba(15, 23, 42, 0.6);
      margin-bottom: 4px;
    }

    &__plan {
      font-size: 20px;
      font-weight: 900;
      letter-spacing: -0.3px;
      color: rgba(15, 23, 42, 0.94);
      line-height: 1.15;
    }

    &__status {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 900;
      letter-spacing: 0.2px;
      border: 1px solid transparent;

      &--success {
        background: rgba(34, 197, 94, 0.12);
        color: rgba(20, 83, 45, 0.98);
        border-color: rgba(34, 197, 94, 0.18);
      }

      &--warning {
        background: rgba(245, 158, 11, 0.14);
        color: rgba(120, 53, 15, 0.98);
        border-color: rgba(245, 158, 11, 0.22);
      }

      &--info {
        background: rgba(59, 130, 246, 0.12);
        color: rgba(30, 58, 138, 0.98);
        border-color: rgba(59, 130, 246, 0.18);
      }

      &--neutral {
        background: rgba(148, 163, 184, 0.14);
        color: rgba(15, 23, 42, 0.78);
        border-color: rgba(148, 163, 184, 0.26);
      }
    }

    &__status-icon {
      opacity: 0.95;
    }

    &__body {
      padding: 12px 16px 14px !important;
    }

    &__facts {
      display: grid;
      grid-template-columns: 1fr;
      gap: 10px;

      @media (min-width: 600px) {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    &__fact {
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 14px;
      padding: 12px 12px;
      background: rgba(255, 255, 255, 0.9);
    }

    &__fact-label {
      font-size: 12px;
      font-weight: 900;
      color: rgba(15, 23, 42, 0.58);
      margin-bottom: 4px;
      letter-spacing: 0.15px;
    }

    &__fact-value {
      font-size: 14px;
      font-weight: 900;
      color: rgba(15, 23, 42, 0.88);
      letter-spacing: -0.1px;
    }

    &__fineprint {
      margin-top: 12px;
      font-size: 12px;
      color: rgba(15, 23, 42, 0.56);
      font-weight: 700;
      line-height: 1.35;
    }
  }
</style>
